import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Trash2, ArrowRight, ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import InstrumentSVG from "@/components/InstrumentSVG";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { parseLocaleFromPath, localePath } from "@/lib/locale";
import { formatEurPlain } from "@/lib/money";

const L = {
  hr: { title: "Vaša košarica", subtitle: "Pregledajte odabrane instrumente prije plaćanja.", empty: "Vaša košarica je prazna.", continue: "Pregledaj modele", checkout: "Nastavi na plaćanje", remove: "Ukloni", subtotal: "Međuzbroj", shipping: "Dostava (procjena pri plaćanju)", total: "Ukupno", back: "Natrag" },
  en: { title: "Your cart", subtitle: "Review your selected instruments before checkout.", empty: "Your cart is empty.", continue: "Browse models", checkout: "Proceed to checkout", remove: "Remove", subtotal: "Subtotal", shipping: "Shipping (calculated at checkout)", total: "Total", back: "Back" },
  de: { title: "Ihr Warenkorb", subtitle: "Überprüfen Sie Ihre Auswahl vor der Kasse.", empty: "Ihr Warenkorb ist leer.", continue: "Modelle ansehen", checkout: "Zur Kasse", remove: "Entfernen", subtotal: "Zwischensumme", shipping: "Versand (an der Kasse berechnet)", total: "Gesamt", back: "Zurück" },
  fr: { title: "Votre panier", subtitle: "Vérifiez vos instruments avant le paiement.", empty: "Votre panier est vide.", continue: "Voir les modèles", checkout: "Procéder au paiement", remove: "Retirer", subtotal: "Sous-total", shipping: "Livraison (calculée au paiement)", total: "Total", back: "Retour" },
};

export default function Cart() {
  const { items, total, removeItem } = useCart();
  const navigate = useNavigate();
  const { locale } = parseLocaleFromPath(useLocation().pathname);
  const t = L[locale] || L.hr;

  return (
    <>
      <SEO title={`${t.title} — Tambure Žmegač`} description={t.subtitle} />
      <section className="bg-background grain-bg">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 py-16">
          <Link to={localePath(locale, "/models")} data-testid="cart-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6">
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> {t.back}
          </Link>
          <h1 className="font-serif text-5xl text-foreground leading-tight">{t.title}</h1>
          <p className="mt-2 text-base text-foreground/70">{t.subtitle}</p>

          {items.length === 0 ? (
            <div className="mt-12 rounded-lg border border-dashed border-border bg-card p-14 text-center" data-testid="cart-page-empty">
              <ShoppingBag className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" strokeWidth={1.2} />
              <p className="text-foreground/70">{t.empty}</p>
              <Link to={localePath(locale, "/models")} className="mt-5 inline-block">
                <Button variant="outline">{t.continue}</Button>
              </Link>
            </div>
          ) : (
            <div className="mt-10 grid lg:grid-cols-[1.4fr_1fr] gap-8">
              <ul className="space-y-3" data-testid="cart-page-items">
                {items.map((it) => (
                  <li key={it.id} className="rounded-lg border border-border/60 bg-card shadow-ambient p-4 flex gap-4">
                    <div className="w-24 h-32 shrink-0 rounded-md overflow-hidden bg-muted">
                      <InstrumentSVG type={it.product_slug} config={it.preview_config || {}} engravingText={it.engraving_text || ""} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <div>
                          <div className="font-serif text-xl text-foreground">{it.product_name}</div>
                          <div className="text-xs text-muted-foreground uppercase tracking-[0.15em] mt-0.5">{it.product_slug}</div>
                        </div>
                        <div className="font-serif text-xl text-primary">{formatEurPlain(it.unit_price_eur)}</div>
                      </div>
                      <ul className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-foreground/70">
                        {(it.configuration || []).filter((c) => c.value_label).map((c) => (
                          <li key={c.field_id} className="truncate">
                            <span className="text-muted-foreground">{c.field_label}:</span> {c.value_label}
                          </li>
                        ))}
                      </ul>
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => removeItem(it.id)}
                          data-testid={`cart-page-remove-${it.id}`}
                          className="inline-flex items-center gap-1.5 text-xs text-destructive hover:text-destructive/80"
                        >
                          <Trash2 className="h-3 w-3" strokeWidth={1.6} /> {t.remove}
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              <aside className="lg:sticky lg:top-24">
                <div className="rounded-lg bg-tz-espresso text-tz-cream shadow-ambient p-6">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-tz-cream/70">{t.subtotal}</span>
                      <span className="text-tz-cream">{formatEurPlain(total)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-tz-cream/70">{t.shipping}</span>
                      <span className="text-tz-cream/60 italic">—</span>
                    </div>
                  </div>
                  <div className="mt-5 pt-5 border-t border-white/10 flex items-baseline justify-between" data-testid="cart-page-total">
                    <span className="uppercase tracking-[0.18em] text-tz-cream/70 text-xs">{t.total}</span>
                    <span className="font-serif text-3xl text-primary">{formatEurPlain(total)}</span>
                  </div>
                  <Button
                    onClick={() => navigate(localePath(locale, "/checkout"))}
                    data-testid="cart-page-checkout-btn"
                    className="mt-5 w-full h-11 shadow-interactive"
                  >
                    {t.checkout} <ArrowRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
                  </Button>
                </div>
              </aside>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
